/**
 * Memorial workspace — Memory Soundtrack (multiple music links + featured track).
 */
import React from 'react';
import {
  ArrowDown, ArrowUp, Music, Plus, Star, Trash2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  detectMusicPlatform,
  getMusicPlatformLabel,
  MUSIC_PLATFORMS,
  newMusicLink,
} from '../../utils/musicLink';

function moveItem(list, index, direction) {
  const next = [...list];
  const target = index + direction;
  if (target < 0 || target >= next.length) return list;
  [next[index], next[target]] = [next[target], next[index]];
  return next.map((item, i) => ({ ...item, sort_order: i }));
}

export function MemorialMusicLinksPanel({ form, setForm }) {
  const links = form.music_links || [];

  const updateLinks = (next) => {
    setForm((p) => ({ ...p, music_links: next }));
  };

  const addLink = () => {
    updateLinks([...links, newMusicLink({ sort_order: links.length, is_featured: links.length === 0 })]);
  };

  const updateLink = (index, patch) => {
    const next = links.map((item, i) => {
      if (i !== index) return item;
      const merged = { ...item, ...patch };
      if (patch.link_url !== undefined) {
        merged.platform = detectMusicPlatform(patch.link_url);
      }
      return merged;
    });
    updateLinks(next);
  };

  const removeLink = (index) => {
    const next = links.filter((_, i) => i !== index);
    if (next.length && !next.some((m) => m.is_featured)) {
      next[0] = { ...next[0], is_featured: true };
    }
    updateLinks(next.map((item, i) => ({ ...item, sort_order: i })));
  };

  const setFeatured = (index) => {
    updateLinks(links.map((item, i) => ({ ...item, is_featured: i === index })));
  };

  return (
    <div className="space-y-5" data-testid="memorial-music-panel">
      <div className="rounded-2xl border border-[hsl(38_14%_86%)]/60 bg-[hsl(42_28%_99%)]/85 p-5">
        <div className="flex items-start gap-3">
          <Music className="mt-0.5 h-4 w-4 text-[#8B7355]" aria-hidden />
          <div>
            <h3 className="font-serif text-base font-light text-[#1c1917]">Memory Soundtrack</h3>
            <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground/90">
              Add songs or music links connected to the person. Choose one featured track for the public memorial page.
            </p>
            <p className="mt-2 text-[11px] text-amber-800/90">
              Only upload or link music you have permission to use. Audio upload can be added later — for now, add a music link.
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          {links.length === 0 ? (
            <p className="text-sm text-muted-foreground">No songs added yet.</p>
          ) : (
            links.map((item, index) => (
              <div
                key={item.id || `music-${index}`}
                className="rounded-xl border border-border/50 bg-white/70 p-4 space-y-3"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setFeatured(index)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${
                      item.is_featured
                        ? 'bg-[hsl(38_36%_42%)] text-white'
                        : 'border border-border/60 text-muted-foreground hover:bg-muted/40'
                    }`}
                  >
                    <Star className={`h-3 w-3 ${item.is_featured ? 'fill-current' : ''}`} />
                    {item.is_featured ? 'Featured soundtrack' : 'Set as featured'}
                  </button>
                  <div className="flex items-center gap-1">
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={index === 0} onClick={() => updateLinks(moveItem(links, index, -1))} aria-label="Move up">
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={index === links.length - 1} onClick={() => updateLinks(moveItem(links, index, 1))} aria-label="Move down">
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => removeLink(index)} aria-label="Remove song">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Song title</Label>
                    <Input value={item.title} onChange={(e) => updateLink(index, { title: e.target.value })} placeholder="Their favourite song" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Artist (optional)</Label>
                    <Input value={item.artist || ''} onChange={(e) => updateLink(index, { artist: e.target.value })} placeholder="Artist name" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Music link</Label>
                  <Input
                    value={item.link_url}
                    onChange={(e) => updateLink(index, { link_url: e.target.value })}
                    placeholder="https://open.spotify.com/track/… or YouTube, Apple Music…"
                  />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Platform</Label>
                    <Select value={item.platform || 'other'} onValueChange={(v) => updateLink(index, { platform: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MUSIC_PLATFORMS.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {item.link_url ? (
                      <p className="text-[10px] text-muted-foreground">
                        Detected: {getMusicPlatformLabel(detectMusicPlatform(item.link_url))}
                      </p>
                    ) : null}
                  </div>
                  <label className="flex items-center justify-between gap-3 pt-6">
                    <span className="text-sm">Visible on public page</span>
                    <Switch
                      checked={item.visible_publicly !== false}
                      onCheckedChange={(v) => updateLink(index, { visible_publicly: v })}
                    />
                  </label>
                </div>

                <div className="space-y-1.5">
                  <Label>Why this song mattered (optional)</Label>
                  <Textarea
                    value={item.note || ''}
                    onChange={(e) => updateLink(index, { note: e.target.value })}
                    rows={2}
                    className="resize-none"
                    placeholder="Wedding dance, road trips, Sunday mornings…"
                  />
                </div>
              </div>
            ))
          )}

          <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={addLink}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add song link
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-[hsl(38_14%_86%)]/60 bg-[hsl(42_28%_99%)]/85 p-5">
        <h4 className="text-sm font-medium text-[#1c1917]">Voice memories</h4>
        <p className="mt-1 text-xs text-muted-foreground">Preserved voice recordings from your archive can appear in Memory Soundtrack on the public page.</p>
        <label className="mt-4 flex items-center justify-between gap-3">
          <span className="text-sm">Show preserved voice memories</span>
          <Switch checked={form.allow_voice} onCheckedChange={(v) => setForm((p) => ({ ...p, allow_voice: v }))} />
        </label>
      </div>
    </div>
  );
}

export default MemorialMusicLinksPanel;
